"use client";

import { ChevronDown } from "lucide-react";
import { useEffect, useId, useRef, useState, type CSSProperties } from "react";

import { CcTrafficSliderField } from "@/components/services/seo/CcTrafficSliderField";
import {
  formatWholeNumber,
  parsePercent,
  parseWholeNumber,
  type RevenueField,
  calculatorCurrency,
  moneyRange,
} from "@/components/services/seo/seo-revenue-projection";
import { CcButton } from "@/components/ui/CcButton";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";

import type { LeadGenDemoCopy } from "./lead-gen-demo-copy";
import { MEETING_CALCULATOR_RANGES, computeMeetingValue } from "./meeting-value";

type CcMeetingCalculatorProps = {
  copy: LeadGenDemoCopy["calculator"];
  locale: string;
};

/**
 * "Vad är ett möte värt?": meetings per month, close rate, deal value and
 * customer lifetime → the value of one booked meeting, new customers and
 * revenue per year. Same layout and styles as the SEO revenue calculator
 * (v2: centred header, sliders left, results right); formulas behind
 * "Så räknar vi". Defaults are examples; the visitor fills in their own.
 */
export function CcMeetingCalculator({ copy, locale }: CcMeetingCalculatorProps) {
  const ids = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const ranges = { ...MEETING_CALCULATOR_RANGES, value: moneyRange(MEETING_CALCULATOR_RANGES.value, locale) };
  const money = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: calculatorCurrency(locale),
    maximumFractionDigits: 0,
  });

  const [meetings, setMeetings] = useState(String(ranges.meetings.fallback));
  const [close, setClose] = useState(String(ranges.close.fallback));
  const [value, setValue] = useState(formatWholeNumber(ranges.value.fallback, locale));
  const [lifetime, setLifetime] = useState(String(ranges.lifetime.fallback));
  const [howOpen, setHowOpen] = useState(false);
  const resultRef = useRef<HTMLDivElement | null>(null);

  // Bars grow in once the results are on screen (same hook as the SEO calculator).
  useEffect(() => {
    const node = resultRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        observer.disconnect();
        node.dataset.inview = "true";
      },
      { threshold: 0.28 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const result = computeMeetingValue({
    meetings: parseWholeNumber(meetings) ?? 0,
    closePercent: parsePercent(close) ?? 0,
    value: parseWholeNumber(value) ?? 0,
    lifetime: parseWholeNumber(lifetime) ?? 1,
  });
  const max = Math.max(1, ...result.years);

  // The slider field styles itself per field name; these map onto the SEO calculator's.
  const slider = (
    field: RevenueField,
    key: keyof typeof ranges,
    label: string,
    raw: string,
    onChange: (next: string) => void,
    kind: "whole" | "percent",
    unit?: string,
  ) => (
    <CcTrafficSliderField
      id={`${ids}-${key}`}
      field={field}
      label={label}
      raw={raw}
      onChange={onChange}
      kind={kind}
      range={ranges[key]}
      locale={locale}
      unit={unit ?? (kind === "percent" ? "%" : undefined)}
    />
  );

  return (
    <section
      data-analytics-tool="meetings_calculator"
      className="cc-section cc-lead-calculator"
      aria-labelledby={`${ids}-title`}
      data-cc-nav="light"
      {...devSectionAttr("lead-calculator")}
    >
      <div className="cc-container">
        <div className="cc-traffic" data-traffic-calculator-version={2}>
          <header className="cc-traffic__header">
            <CcSanjayaEyebrow theme="on-subtle">{copy.eyebrow}</CcSanjayaEyebrow>
            <h2 id={`${ids}-title`} className="cc-traffic__title">
              {copy.title}
            </h2>
            <p className="cc-traffic__text">{copy.text}</p>
          </header>

          <div className="cc-traffic__columns">
            <div className="cc-traffic__side">
              <div className="cc-traffic__stack">
                <div className="cc-traffic__controls">
                  {slider("visits", "meetings", copy.meetingsLabel, meetings, setMeetings, "whole")}
                  {slider("close", "close", copy.closeLabel, close, setClose, "percent")}
                  {slider("value", "value", copy.valueLabel, value, setValue, "whole", copy.valueUnit)}
                  {slider("growth", "lifetime", copy.lifetimeLabel, lifetime, setLifetime, "whole", copy.lifetimeUnit)}
                </div>
                <div className="cc-traffic__cta cc-lead-calculator__cta">
                  <CcButton
                    bookCallPopup
                    bookCallVariant="default"
                    label={copy.cta}
                    expand
                    expandCompact
                    className="cc-traffic__button"
                  />
                </div>
              </div>
            </div>

            <div className="cc-traffic__panel cc-traffic__panel--results">
              <div
                ref={resultRef}
                className="cc-traffic__result cc-traffic__result--impact-v2"
                aria-live="polite"
              >
                <p className="cc-traffic__impact-eyebrow">{copy.impactEyebrow}</p>
                <div className="cc-traffic__impact-hero">
                  <p className="cc-traffic__impact-hero-sub">{copy.heroLabel}</p>
                  <p className="cc-traffic__impact-hero-value">
                    {money.format(Math.round(result.meetingValue))}
                  </p>
                  <p className="cc-traffic__impact-hero-secondary">{copy.heroSub}</p>
                </div>
                <dl className="cc-traffic__impact-stats">
                  <div className="cc-traffic__impact-stat">
                    <dd className="cc-traffic__impact-stat-value">
                      {formatWholeNumber(Math.round(result.customersPerYear), locale)}
                    </dd>
                    <dt className="cc-traffic__impact-stat-label">{copy.customersLabel}</dt>
                  </div>
                  <div className="cc-traffic__impact-stat">
                    <dd className="cc-traffic__impact-stat-value">
                      {money.format(Math.round(result.yearOneRevenue))}
                    </dd>
                    <dt className="cc-traffic__impact-stat-label">{copy.yearRevenueLabel}</dt>
                  </div>
                  <div className="cc-traffic__impact-stat">
                    <dd className="cc-traffic__impact-stat-value">
                      {money.format(Math.round(result.customerValue))}
                    </dd>
                    <dt className="cc-traffic__impact-stat-label">{copy.lifetimeValueLabel}</dt>
                  </div>
                </dl>

                <p className="cc-traffic__chart-title cc-traffic__chart-title--impact">
                  {copy.chartTitle}
                </p>
                <ol className="cc-traffic__bars">
                  {result.years.map((amount, index) => (
                    <li key={index} className="cc-traffic__bar">
                      <span className="cc-traffic__bar-track">
                        <span className="cc-traffic__bar-value">
                          {money.format(Math.round(amount))}
                        </span>
                        <span
                          className="cc-traffic__bar-fill"
                          aria-hidden="true"
                          style={{ "--cc-bar": Math.max(0.02, amount / max) } as CSSProperties}
                        />
                      </span>
                      <span className="cc-traffic__bar-label">
                        {copy.yearLabel.replace("{n}", String(index + 1))}
                      </span>
                    </li>
                  ))}
                </ol>

                <p className="cc-traffic__help">{copy.footnote}</p>

                <div className="cc-traffic__how">
                  <button
                    type="button"
                    className="cc-traffic__toggle"
                    aria-expanded={howOpen}
                    aria-controls={`${ids}-how`}
                    onClick={() => setHowOpen((open) => !open)}
                  >
                    {copy.howLink}
                    <ChevronDown size={14} strokeWidth={2} aria-hidden />
                  </button>
                  <div
                    id={`${ids}-how`}
                    className="cc-traffic__how-body"
                    data-open={howOpen ? "true" : "false"}
                    aria-hidden={!howOpen}
                  >
                    <div className="cc-traffic__how-collapse">
                      <div className="cc-traffic__how-content" inert={howOpen ? undefined : true}>
                        {copy.how.map((line) => (
                          <p key={line}>{line}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
